import { useEffect, useState, useCallback } from 'react';
import { useAdmin } from '@/hooks/useAdmin';
import { Save, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TABS = [
  { key: 'general',    label: 'Opće',          fields: [{ k: 'site_name', t: 'Naziv sitea' }, { k: 'tagline', t: 'Slogan' }, { k: 'default_language', t: 'Zadani jezik' }, { k: 'contact_email', t: 'Kontakt e-mail' }, { k: 'contact_phone', t: 'Kontakt telefon' }] },
  { key: 'branding',   label: 'Branding',      fields: [{ k: 'header_logo_url', t: 'Header logo URL' }, { k: 'footer_logo_url', t: 'Footer logo URL' }, { k: 'og_default_image', t: 'Default OG image' }, { k: 'favicon_url', t: 'Favicon URL' }] },
  { key: 'appearance', label: 'Izgled',        fields: [{ k: 'default_theme', t: 'Default tema (auto/light/dark)' }, { k: 'accent_color', t: 'Akcent boja (#hex)' }] },
  { key: 'seo',        label: 'SEO',           fields: [{ k: 'default_description', t: 'Zadani meta opis', textarea: true }, { k: 'default_og_image', t: 'Default OG image' }, { k: 'robots', t: 'Robots' }, { k: 'sitemap_enabled', t: 'Sitemap omogućen', bool: true }, { k: 'gsc_verification', t: 'GSC verification code' }, { k: 'ga4_id', t: 'GA4 ID' }, { k: 'plausible_domain', t: 'Plausible domena' }] },
  { key: 'ads',        label: 'Oglasi',        fields: [{ k: 'adsense_publisher_id', t: 'AdSense publisher ID' }, { k: 'adsense_in_article_slot', t: 'In-article slot ID' }, { k: 'adsense_sidebar_slot', t: 'Sidebar slot ID' }, { k: 'adsense_header_slot', t: 'Header slot ID' }, { k: 'show_placeholders', t: 'Prikaži placeholder oglase', bool: true }, { k: 'cpm_baseline_eur', t: 'CPM baseline (EUR)', num: true }] },
  { key: 'payments',   label: 'Plaćanja',      fields: [{ k: 'stripe_publishable_key', t: 'Stripe publishable key' }, { k: 'currency', t: 'Valuta (eur/usd)' }] },
  { key: 'social',     label: 'Socijalne mreže', fields: [{ k: 'facebook', t: 'Facebook URL' }, { k: 'twitter', t: 'Twitter URL' }, { k: 'linkedin', t: 'LinkedIn URL' }, { k: 'youtube', t: 'YouTube URL' }, { k: 'instagram', t: 'Instagram URL' }] },
  { key: 'newsletter', label: 'Newsletter',    fields: [{ k: 'smtp_host', t: 'SMTP host' }, { k: 'smtp_port', t: 'SMTP port', num: true }, { k: 'smtp_user', t: 'SMTP user' }, { k: 'smtp_pass', t: 'SMTP lozinka' }, { k: 'from_email', t: 'From e-mail' }, { k: 'confirmation_template', t: 'Confirmation template', textarea: true }] },
  { key: 'homepage',   label: 'Naslovnica',    fields: [{ k: 'hero_card_count', t: 'Broj hero kartica', num: true }, { k: 'submit_section_video', t: 'Submit section video URL' }, { k: 'show_breaking_ticker', t: 'Prikaži breaking ticker', bool: true }] },
  { key: 'attributes', label: 'Atributi',      fields: [{ k: 'allowed_flags', t: 'Allowed article flags (comma-separated)', list: true }] },
  { key: 'maintenance', label: 'Održavanje',   fields: [
    { k: 'enabled', t: 'Stavi portal u održavanje (samo admini vide normalan portal)', bool: true },
    { k: 'message', t: 'Poruka koju vidi posjetitelj', textarea: true },
  ] },
];

export default function SiteSettings() {
  const { authFetch } = useAdmin();
  const [tab, setTab] = useState('general');
  const [doc, setDoc] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try { const r = await authFetch(`${API}/admin/settings`); setDoc(await r.json()); } catch {}
  }, [authFetch]);

  useEffect(() => { load(); }, [load]);

  function setField(section, key, value) {
    setDoc(d => ({ ...d, [section]: { ...(d[section] || {}), [key]: value } }));
  }

  async function saveSection(section) {
    setSaving(true);
    try {
      const r = await authFetch(`${API}/admin/settings/${section}`, { method: 'PUT', body: JSON.stringify({ data: doc[section] }) });
      if (r.ok) { toast.success('Spremljeno'); await load(); }
      else toast.error('Greška pri spremanju');
    } finally { setSaving(false); }
  }

  if (!doc) return <div className="p-6 text-zinc-400">Učitavam…</div>;

  const active = TABS.find(t => t.key === tab);

  return (
    <div className="p-6" data-testid="site-settings-page">
      <h1 className="text-2xl font-semibold mb-1">Postavke sitea</h1>
      <p className="text-xs text-zinc-400 mb-5">Centralizirane postavke po kategorijama. Spremaj promjene odvojeno po tabu.</p>

      {/* Tabs */}
      <div className="flex flex-wrap gap-1 border-b border-zinc-800 mb-5">
        {TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`px-4 py-2 text-sm font-medium -mb-px border-b-2 transition-colors ${tab === t.key ? 'border-red-600 text-red-400' : 'border-transparent text-zinc-400 hover:text-zinc-200'}`}
            data-testid={`settings-tab-${t.key}`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Form */}
      <form onSubmit={(e) => { e.preventDefault(); saveSection(active.key); }} className="space-y-4 max-w-2xl">
        {active.fields.map(f => (
          <div key={f.k}>
            <label className="block text-xs text-zinc-400 mb-1">{f.t}</label>
            {f.bool ? (
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={!!doc[active.key]?.[f.k]} onChange={e => setField(active.key, f.k, e.target.checked)} />
                <span>{f.t}</span>
              </label>
            ) : f.textarea ? (
              <textarea rows={4} value={doc[active.key]?.[f.k] || ''} onChange={e => setField(active.key, f.k, e.target.value)}
                className="w-full bg-zinc-950 border border-zinc-700 rounded-md px-3 py-2 text-sm" data-testid={`settings-field-${f.k}`} />
            ) : f.list ? (
              <input value={Array.isArray(doc[active.key]?.[f.k]) ? doc[active.key][f.k].join(', ') : (doc[active.key]?.[f.k] || '')}
                onChange={e => setField(active.key, f.k, e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
                className="w-full bg-zinc-950 border border-zinc-700 rounded-md px-3 py-2 text-sm" data-testid={`settings-field-${f.k}`} />
            ) : (
              <input type={f.num ? 'number' : 'text'} value={doc[active.key]?.[f.k] ?? ''}
                onChange={e => setField(active.key, f.k, f.num ? Number(e.target.value) : e.target.value)}
                className="w-full bg-zinc-950 border border-zinc-700 rounded-md px-3 py-2 text-sm" data-testid={`settings-field-${f.k}`} />
            )}
          </div>
        ))}
        <button type="submit" disabled={saving}
          className="px-4 py-2 bg-red-600 hover:bg-red-700 rounded-md text-white font-medium flex items-center gap-2 disabled:opacity-50"
          data-testid={`settings-save-${active.key}`}>
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Spremi {active.label}
        </button>
      </form>
    </div>
  );
}
